import { useState } from 'react';
import { cn } from 'cn';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import {
  fetchKhutbahs,
  fetchKhutbahsByIds,
  type KhutbahCard,
} from '@/features/home/homeApi';
import { useUrlParam } from '@/features/reader/useUrlState';
import { useAssistantLanguage } from '@/features/settings/useAssistantLanguage';
import { ChatPanel } from './ChatPanel';

/**
 * Ask about a published khutbah, from anywhere in the product.
 *
 * **The scope is the whole design.** The Chat mock's first rule is "the
 * boundary is visible, not implied ... Answers come only from this text", and
 * on a screen reached from the rail rather than from a khutbah, the boundary
 * is the thing a reader is most likely to get wrong. So the khutbah is chosen
 * explicitly, named in a bordered scope box above the thread, and the picker
 * never leaves the screen — there is no state in which a question is asked
 * and it is unclear what it was asked about.
 *
 * **One khutbah at a time, and that is a real limit, not a design choice.**
 * ADR 0010 decided that a library-wide chat would use pgvector over the
 * passage embeddings; the embeddings are produced, but the retrieval endpoint
 * does not exist and §9's four strategies are still an open evaluation. The
 * endpoint that DOES exist is per-khutbah
 * (`/api/public/khutbah/{id}/chat`), and it sends the whole khutbah with no
 * retrieval at all. Answering "across the library" would mean inventing an
 * endpoint, so this screen asks the reader to pick instead and says why.
 *
 * Nothing here is a second chat implementation: it is the same `ChatPanel`
 * the reader docks, with the same grounding, the same refusals and the same
 * "not answering yet" state when no provider is configured.
 */

function formatFriday(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function KhutbahChoice({
  card,
  selected,
  onSelect,
}: {
  card: KhutbahCard;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        className={cn(
          'flex w-full flex-col items-start gap-1 rounded-md px-2 py-2 text-start',
          'hover:bg-sidebar-accent',
          selected ? 'bg-sidebar-accent text-sidebar-primary' : 'text-foreground',
        )}
      >
        <span className="text-[11px] text-muted-foreground">
          {formatFriday(card.friday)} · {card.institutionName}
        </span>
        {card.titleOriginal ? (
          <span
            lang="ar"
            dir="rtl"
            className="script-ar w-full text-sm text-reading-foreground"
          >
            {card.titleOriginal}
          </span>
        ) : (
          <span className="text-sm">Khutbah of {formatFriday(card.friday)}</span>
        )}
      </button>
    </li>
  );
}

export function ChatScreen() {
  const [khutbahId, setKhutbahId] = useUrlParam('khutbah', '');
  // The Assistant language setting was written by Settings and read by
  // nobody: a reader with reading=Malayalam and assistant=English still got
  // Malayalam answers. It resolves here, and "same as reading" means exactly
  // that rather than a second place the reading language has to be repeated.
  const language = useAssistantLanguage();
  const [pickerOpen, setPickerOpen] = useState(false);

  const khutbahs = useQuery({
    queryKey: ['khutbahs', 'chat'],
    queryFn: ({ signal }) => fetchKhutbahs({ limit: 50 }, signal),
    staleTime: 5 * 60 * 1000,
  });

  const all = khutbahs.data ?? [];

  // A khutbah named in a link is not necessarily in the newest 50. Looking
  // for it only there meant an older shared chat link could not select its
  // own document, and the screen asked the reader to choose one they had
  // already chosen.
  const onFirstPage = all.some((card) => card.khutbahId === khutbahId);
  const named = useQuery({
    queryKey: ['khutbahs', 'by-id', [khutbahId]],
    queryFn: ({ signal }) => fetchKhutbahsByIds([khutbahId], signal),
    enabled: khutbahId !== '' && !onFirstPage && khutbahs.isSuccess,
    staleTime: 5 * 60 * 1000,
  });

  const selected =
    all.find((card) => card.khutbahId === khutbahId)
    ?? named.data?.find((card) => card.khutbahId === khutbahId)
    ?? null;

  const picker = (
    <ul className="flex flex-col gap-0.5">
      {all.map((card) => (
        <KhutbahChoice
          key={card.khutbahId}
          card={card}
          selected={card.khutbahId === khutbahId}
          onSelect={() => {
            setKhutbahId(card.khutbahId);
            setPickerOpen(false);
          }}
        />
      ))}
    </ul>
  );

  return (
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden">
      <IconRail />

      {/* The choice, always on screen at desktop: the scope must never be
          something a reader has to remember. */}
      <nav
        aria-label="Choose a khutbah"
        className="hidden w-64 shrink-0 flex-col gap-1 overflow-y-auto border-e border-sidebar-border bg-sidebar px-2 py-4 lg:flex"
      >
        <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Ask about
        </p>
        {khutbahs.isPending ? (
          <div className="flex flex-col gap-2 px-2" aria-busy>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          picker
        )}
      </nav>

      <main className="flex min-h-0 flex-col flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 sm:px-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Ask AI</h1>
            <p className="mt-1 text-sm text-pretty text-muted-foreground">
              Questions are answered from one published khutbah&rsquo;s own text.
              Choose which one.
            </p>
          </div>

          {/* Below lg the picker is a disclosure rather than a column. */}
          <div className="lg:hidden">
            <button
              type="button"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((was) => !was)}
              className="min-h-9 rounded-md border border-input px-3 text-sm"
            >
              {selected ? 'Change the khutbah' : 'Choose a khutbah'}
            </button>
            {pickerOpen ? (
              <div className="mt-2 rounded-lg border border-border p-2">{picker}</div>
            ) : null}
          </div>

          {selected === null ? (
            <div className="rounded-lg border border-dashed border-border p-6">
              <h2 className="text-base font-semibold">
                Choose a khutbah to ask about
              </h2>
              <p className="mt-2 text-sm text-pretty text-muted-foreground">
                The assistant answers only from the text of the khutbah you pick
                — not from the web, not from general knowledge, and never with a
                ruling. Picking one is what makes the answer checkable.
              </p>
              <p className="mt-2 text-sm text-pretty text-muted-foreground">
                Asking one question across every khutbah at once is not built
                yet. It needs retrieval over the whole library, which is a later
                phase.
              </p>
            </div>
          ) : (
            <>
              {/*
                The scope box the mock requires: the document, not the word
                "Chat". A reader must be able to see what the answer will be
                grounded in without scrolling or remembering.
              */}
              <div className="rounded-lg border border-primary bg-card p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Answers come only from this khutbah
                </p>
                {selected.titleOriginal ? (
                  <p
                    lang="ar"
                    dir="rtl"
                    className="script-ar mt-1.5 text-lg text-reading-foreground"
                  >
                    {selected.titleOriginal}
                  </p>
                ) : null}
                <p className="mt-1 text-sm text-muted-foreground">
                  {formatFriday(selected.friday)} · {selected.institutionName}
                  {' · '}
                  {selected.sourceKind === 'publisher'
                    ? 'Publisher-sourced'
                    : 'User-submitted'}
                </p>
                <a
                  href={`?khutbah=${encodeURIComponent(selected.khutbahId)}`}
                  className="mt-2 inline-block text-sm text-primary underline"
                >
                  Read it
                </a>
              </div>

              {/* The same panel the reader docks — not a second implementation. */}
              <div className="rounded-lg border border-border">
                <ChatPanel
                  // Remounts when the khutbah changes, so an answer about the
                  // previous one can never still be on screen under a new
                  // scope box.
                  key={selected.khutbahId}
                  khutbahId={selected.khutbahId}
                  language={language}
                  // The panel renders a "Passage N" button for every
                  // citation. Without a handler those buttons did nothing —
                  // the one affordance that lets a reader check a claim
                  // against its source, and it was inert on this screen.
                  //
                  // The reader is where a passage can be read in context, so
                  // the button goes there. The ordinal travels in the URL and
                  // the reader resolves it to a passage id, because a
                  // citation knows its number and not the khutbah's ids.
                  onCitation={(ordinal) => {
                    const url =
                      `?khutbah=${encodeURIComponent(selected.khutbahId)}`
                      + `&language=${encodeURIComponent(language)}`
                      + `&passage=${ordinal}`;
                    window.location.assign(url);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
